// first-accessible-path.js
//
// Resolves the landing route for a logged-in user by walking their
// permission-driven `user.menu` (same shape config-navigation.js consumes)
// and returning the path of the first top-level item they can reach.
//
// Uses:
//   - GuestGuard: sends already-authenticated visitors landing on `/` here
//     instead of a hard-coded `/dashboard`. That way a user without the
//     "summary/dashboard" permission doesn't get bounced to a blank
//     403-ish page — they land on whatever their first sidebar item is
//     (e.g. Proposals for a proposals-only user, Talents for a talent
//     manager, etc.).
//   - Logo click (which links to `/`) inherits the same behaviour.
//
// If `user.menu` is missing / empty, or the dotted path can't be resolved
// against `paths`, we fall back to `paths.dashboard.root` (the same value
// used before this helper existed). Callers stay safe.

import { paths } from 'src/routes/paths';

// Resolve a dotted path spec ("dashboard.proposals.list") against the
// paths tree — mirrors the walk in layouts/dashboard/config-navigation.js
// so both places agree on which URL a menu item maps to.
function resolveDottedPath(spec) {
  if (typeof spec !== 'string' || !spec.trim()) return null;
  const parts = spec.trim().split('.');
  let node = paths;
  for (let i = 1; i < parts.length; i += 1) {
    if (node && node[parts[i]] !== undefined) {
      node = node[parts[i]];
    } else {
      return null;
    }
  }
  return typeof node === 'string' ? node : null;
}

export function getFirstAccessiblePath(user) {
  const fallback = paths.dashboard.root;
  const menu = user?.menu;
  if (!Array.isArray(menu) || menu.length === 0) return fallback;

  // Consider only top-level items (parent_id === 0) the user has READ
  // access to. Mirrors the read_access filter in config-navigation.js
  // so we never redirect the user to a route that will 404 them via
  // PermissionGuard.
  const hasReadAccess = (m) =>
    m.read_access === true || m.read_access === 1 || m.read_access === '1';
  const topLevel = menu.filter(
    (m) => m && m.parent_id === 0 && m.path && hasReadAccess(m)
  );
  if (topLevel.length === 0) return fallback;

  topLevel.sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity));
  for (const item of topLevel) {
    const resolved = resolveDottedPath(item.path);
    if (resolved) return resolved;
  }
  return fallback;
}
